import { gql } from 'graphql-request';

import { graphQLFragments } from 'shared/graphql/fragments';

const addToShoppingCartRecommendedAudiobooks = gql`
  ${graphQLFragments.audiobookShape}
  ${graphQLFragments.productShape}
  query FetchAddToShoppingCartRecommendedAudiobooks($id: ID!, $relatedCount: Int, $dealsCount: Int) {
    audiobook(id: $id) {
      relatedAudiobooks(count: $relatedCount, padType: "popular_tag_deals") {
        ...audiobookFields

        currentProduct {
          ...productFields
        }
      }
    }

    dealsPagePromotionsDataWithTags(sortKey: POPULARITY_DESC, omitUserOwned: true) {
      objects(pageSize: $dealsCount) {
        ... on Promotion {
          audiobook {
            ...audiobookFields
          }

          product {
            ...productFields
          }
        }
      }
    }
  }
`;

const audiobookForAudiobookButtons = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchAudiobook($id: ID!) {
    audiobook(id: $id) {
      ...audiobookWithShoppingCartAndUserAudiobookFields

      currentProduct {
        ...productFields
      }
    }
  }
`;

const audiobookForCollectionsPage = gql`
  ${graphQLFragments.audiobookShape}
  ${graphQLFragments.productShape}
  query FetchAudiobook($id: ID!) {
    audiobook(id: $id) {
      ...audiobookFields
      currentBlurb {
        sanitizedText
      }
      description
      currentProduct {
        ...productFields
      }
      currentUserShoppingCartItem {
        id
      }
      currentUserUserAudiobook {
        id
      }
    }
  }
`;

const audiobookSearch = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query AudiobookSearch($query: String!, $promotionFilter: String, $filter: String, $page: Int, $pageSize: Int) {
    audiobooks(query: $query, promotionFilter: $promotionFilter, filter: $filter, page: $page, pageSize: $pageSize) {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on Audiobook {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          futureSaleDate

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const audiobookTracks = gql`
  query fetchAudiobookTracks($id: ID!) {
    audiobook(id: $id) {
      tracks {
        partNumber
        chapterNumber
        offsetFromBookStartMs
        durationMs
        displayName
      }
    }
  }
`;

const mediaUrl = gql`
  query fetchAudiobookTrackUrl($id: ID!, $partNumber: Int!, $chapterNumber: Int!) {
    audiobook(id: $id) {
      track(partNumber: $partNumber, chapterNumber: $chapterNumber) {
        webPlayerMediaUrl
      }
    }
  }
`;

const authorAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchAuthorAudiobooks(
    $slug: String!
    $page: Int
    $pageSize: Int
    $filterCurrentUserOwned: Boolean
    $hideUnpromotable: Boolean
    $sort: String
  ) {
    author(slug: $slug) {
      paginatedAudiobooks(filterCurrentUserOwned: $filterCurrentUserOwned, sort: $sort, hideUnpromotable: $hideUnpromotable ) {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on Audiobook {
            ...audiobookWithShoppingCartAndUserAudiobookFields

            futureSaleDate

            currentProduct {
              ...productFields
            }
          }
        }
      }
    }
  }
`;

const checkoutDetailsRelatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchCheckoutDetailsRelatedAudiobooks($audiobookIds: [ID!], $limit: Int!, $padType: String) {
    checkoutDetails(audiobookIds: $audiobookIds) {
      relatedAudiobooks(limit: $limit, padType: $padType) {
        ...audiobookWithShoppingCartAndUserAudiobookFields
        relatedAudiobooksType
        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

const collectionAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchCollectionAudiobooks($id: ID!, $page: Int, $pageSize: Int, $sort: String) {
    collection(id: $id) {
      paginatedAudiobooks(sort: $sort, page: $page, pageSize: $pageSize) {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on Audiobook {
            ...audiobookWithShoppingCartAndUserAudiobookFields
            futureSaleDate
            description
            sampleUrl
            bookDescription
            bookDescriptionShort
            editorialDescription
            awardWinnersTags {
              id,
              slug,
              displayName
            }

            currentProduct {
              salableInCurrentCountry
              ...productFields
            }
          }
        }
      }
    }
  }
`;

const currentUserRelatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchCurrentUserRelatedAudiobooks($limit: Int!) {
    currentUserRelatedAudiobooks(limit: $limit) {
      ...audiobookWithShoppingCartAndUserAudiobookFields
      currentProduct {
        ...productFields
      }
    }
  }
`;

const featuredCategoryAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchFeaturedCategoryAudiobooks(
    $tagId: ID!
    $carousel: FeaturedCategoryAudiobooksFilter!
    $page: Int
    $pageSize: Int
    $seed: Int
  ) {
    featuredCategoryAudiobooks(tagId: $tagId, carousel: $carousel, seed: $seed) {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on Audiobook {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const highlyRatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchHighlyRatedAudiobooks($page: Int, $pageSize: Int) {
    highlyRatedAudiobooks {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on Audiobook {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const narratorPaginatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchNarratorAudiobooks(
    $slug: String!
    $page: Int
    $pageSize: Int
    $filterCurrentUserOwned: Boolean
    $sort: String
    $hideUnpromotable: Boolean
  ) {
    narrator(slug: $slug) {
      paginatedAudiobooks(filterCurrentUserOwned: $filterCurrentUserOwned, sort: $sort, hideUnpromotable: $hideUnpromotable ) {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on Audiobook {
            ...audiobookWithShoppingCartAndUserAudiobookFields

            futureSaleDate

            currentProduct {
              ...productFields
            }
          }
        }
      }
    }
  }
`;

const purchaseRelatedCarousels = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchPurchaseRelatedCarousels($id: ID!) {
    purchase(id: $id) {
      relatedCarousels {
        type
        title
        audiobooks {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const recentlyViewedAudiobooks = gql`
  ${graphQLFragments.audiobookShape}
  ${graphQLFragments.productShape}
  query FetchRecentlyViewedAudiobooks($pageSize: Int, $omitAudiobookIds: [ID!]) {
    currentUserRecentAudiobooks(pageSize: $pageSize, omitAudiobookIds: $omitAudiobookIds) {
      ...audiobookFields
      currentProduct {
        ...productFields
      }
    }
  }
`;

const relatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchRelatedAudiobooks($id: ID!, $count: Int, $filterPromoted: Boolean) {
    audiobook(id: $id) {
      relatedAudiobooks(count: $count, filterPromoted: $filterPromoted) {
        ...audiobookWithShoppingCartAndUserAudiobookFields
        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

const seriesAudiobooks = gql`
  ${graphQLFragments.productShape}
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  query fetchSeriesAudiobooks(
    $slug: String!
    $page: Int
    $pageSize: Int
    $filterCurrentUserOwned: Boolean
    $hideUnpromotable: Boolean
  ) {
    series(slug: $slug) {
      paginatedAudiobooks(filterCurrentUserOwned: $filterCurrentUserOwned,  hideUnpromotable: $hideUnpromotable) {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on Audiobook {
            ...audiobookWithShoppingCartAndUserAudiobookFields
            futureSaleDate

            currentProduct {
              ...productFields
            }
          }
        }
      }
    }
  }
`;

const tagCarouselAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchTagCarouselAudiobooks(
    $ids: [ID!]!
    $sort: String
    $filter: String
  ) {
    tags(ids: $ids) {
      id
      displayName
      slug
      carouselAudiobooks(
        sort: $sort
        filter: $filter
      ) {
        ...audiobookWithShoppingCartAndUserAudiobookFields
        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

const tagPaginatedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchTagPaginatedAudiobooks($slug: String!, $sort: String, $filter: String, $excludeAudiobookId: ID, $page: Int, $pageSize: Int) {
    tag(slug: $slug) {
      paginatedAudiobooks(sort: $sort, filter: $filter, excludeAudiobookId: $excludeAudiobookId) {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on Audiobook {
            ...audiobookWithShoppingCartAndUserAudiobookFields
            bookDescription
            bookDescriptionShort

            currentProduct {
              ...productFields
            }
          }
        }
      }
    }
  }
`;

const taggedAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchTaggedAudiobooks($tagInternalNames: [String!]!, $page: Int, $pageSize: Int, $sort: String) {
    taggedAudiobooks(tagInternalNames: $tagInternalNames, sort: $sort) {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on Audiobook {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const taggedNewAndNoteworthyAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchTaggedNewAndNoteworthyAudiobooks($tagId: ID!, $sort: String, $page: Int, $pageSize: Int) {
    taggedNewAndNoteworthyAudiobooks(tagId: $tagId, sort: $sort) {
      totalCount
      objects(page: $page, pageSize: $pageSize) {
        ... on Audiobook {
          ...audiobookWithShoppingCartAndUserAudiobookFields

          currentProduct {
            ...productFields
          }
        }
      }
    }
  }
`;

const unreviewedAudiobooks = gql`
  query FetchUnreviewedBooks($filter: UserAudiobooksFilter, $sort: UserAudiobooksSort) {
    currentUserAudiobooks(filter: $filter, sort: $sort) {
      audiobook {
        id
        coverUrl
        displayTitle
        displayAuthors
      }
    }
  }
`;

const userCategoryEditorsPicksCarousel = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchUserCategoryEditorsPicksCarousel {
    userCategoryEditorsPicksCarousel {
      audiobooks {
        ...audiobookWithShoppingCartAndUserAudiobookFields

        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

const userCategoryPromotionCarousels = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query fetchUserCategoryPromotionCarousels {
    userCategoryPromotionCarousels {
      title
      viewAllLink
      audiobooks {
        ...audiobookWithShoppingCartAndUserAudiobookFields

        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

const userReviews = gql`
  query fetchUserReviews($audiobookId: ID!, $page: Int, $pageSize: Int) {
    audiobook(id: $audiobookId) {
      averageRating
      ratingsCount
      reviewedByCurrentUser
      userReviews {
        totalCount
        objects(page: $page, pageSize: $pageSize) {
          ... on UserReview {
            createdAt
            id
            isCurrentUserReview
            likedByCurrentUser
            likesCount
            rating
            review
            userDisplayName
          }
        }
      }
    }
  }
`;

const seriesPromptAudiobooks = gql`
  ${graphQLFragments.audiobookWithShoppingCartAndUserAudiobookShape}
  ${graphQLFragments.productShape}
  query FetchSeriesPromptAudiobooks($seriesSlug: String!, $audiobookId: ID!) {
    series(slug: $seriesSlug) {
      nextAudiobookForPrompt(audiobookId: $audiobookId) {
        ...audiobookWithShoppingCartAndUserAudiobookFields

        currentProduct {
          ...productFields
        }
      }
      audiobooksForPrompt {
        ...audiobookWithShoppingCartAndUserAudiobookFields

        currentProduct {
          ...productFields
        }
      }
    }
  }
`;

export const audiobookQueries = {
  addToShoppingCartRecommendedAudiobooks,
  audiobookForAudiobookButtons,
  audiobookForCollectionsPage,
  audiobookSearch,
  audiobookTracks,
  authorAudiobooks,
  checkoutDetailsRelatedAudiobooks,
  collectionAudiobooks,
  currentUserRelatedAudiobooks,
  featuredCategoryAudiobooks,
  highlyRatedAudiobooks,
  mediaUrl,
  narratorPaginatedAudiobooks,
  purchaseRelatedCarousels,
  recentlyViewedAudiobooks,
  relatedAudiobooks,
  seriesAudiobooks,
  seriesPromptAudiobooks,
  tagCarouselAudiobooks,
  taggedAudiobooks,
  taggedNewAndNoteworthyAudiobooks,
  tagPaginatedAudiobooks,
  unreviewedAudiobooks,
  userCategoryEditorsPicksCarousel,
  userCategoryPromotionCarousels,
  userReviews,
};
